import { useState, useEffect } from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import HighchartsExporting from 'highcharts/modules/exporting'
import highchartsAccessibility from 'highcharts/modules/accessibility'
import {
  formatNumberOnUnitMeasure,
  formatUnitMeasure,
  formatUnitMult,
  getBadges,
  getUnitMeasureItems,
  getRecentYearRange,
} from '../../../common/utils/helper'
import NoDataImage from '../../../common/components/NoDataImage'
import { useSelector } from 'react-redux'
import { getChartTitle, getSingleYearChartSubtitle } from '../common/helper'
import { disaggregations } from '../../../common/utils/enum'

// Initialize Highcharts modules
HighchartsExporting(Highcharts)
highchartsAccessibility(Highcharts)

Highcharts.setOptions({
  chart: {
    style: {
      fontFamily: '"Open Sans", sansSerif',
    },
  },
})

const PieChart = ({
  chartKey,
  chartOptions = {},
  noDataImageURL,
  selectedCountries,
  selectedAverage,
  chartType,
  unitMeasureName,
  selectedUnitMeasure,
  indicatorMetadata,
  selectedSingleYear,
  setLegendRefFalse,
  countriesData,
  recentYearSelection,
  indicatorDataForChart,
  selectedFilters,
  disaggregationData,
  selectedComparison,
  unitMeasureList,
  site,
}) => {
  const [noData, setNoData] = useState(false)
  const { filteredIndicatorData, filteredAggregateData } = useSelector(
    (state) => state.indicatorProfile
  )
  const dataSource =
    filteredIndicatorData.length > 0
      ? filteredIndicatorData
      : filteredAggregateData

  const shortLabel =
    getUnitMeasureItems(disaggregationData)?.find(
      (item) => item.code === selectedUnitMeasure
    )?.short_label || ''

  // Check for no data
  useEffect(() => {
    const seriesData = chartOptions?.series?.[0]?.data || []
    const noData =
      !seriesData.length || seriesData.every((item) => !item.y || item.y === 0)
    setNoData(noData)
    setLegendRefFalse(noData)
  }, [chartOptions, setLegendRefFalse])

  // Default chart options, merged with provided chartOptions
  const title =
    indicatorMetadata?.admin_metadata?.[site]?.visualization?.display_name ||
    indicatorMetadata?.series_description.name

  const subtitle = getSingleYearChartSubtitle(
    selectedCountries,
    countriesData,
    recentYearSelection
      ? `Most recent values ${getRecentYearRange(indicatorDataForChart)}`
      : selectedSingleYear
  )

  const getPieChartSubtitle = (badges, subtitle) => {
    let badgesDiv =
      badges && badges.length > 0
        ? badges.map(
            (badge) =>
              `<div class="text tui_badge">
        <div>
          <p>${badge}</p>
        </div>
      </div>`
          )
        : []
    badgesDiv = badgesDiv.join(' ')
    return {
      useHTML: true,
      text: `<p class="tui__text_base">${subtitle}</p>
      <div class="tui__badge_container">
        <div class="tui_badge_group">
            ${badgesDiv}
        </div>
      </div>
    `,
      align: 'left',
    }
  }

  const badges = getBadges(
    selectedFilters,
    disaggregationData,
    selectedComparison,
    unitMeasureName
  )

  const options = {
    chart: {
      type: 'pie',
      backgroundColor: 'transparent',
      height: 600,
      // margin: [20, 20, 80, 20],
      events: {
        load: function () {
          const chart = this
          if (noData) {
            chart.renderer
              .text(
                'No data available',
                chart.plotWidth / 2,
                chart.plotHeight / 2
              )
              .attr({ align: 'center' })
              .css({ color: '#666', fontSize: '14px' })
              .add()
          }
        },
      },
    },
    title: getChartTitle(title),
    subtitle: getPieChartSubtitle(badges, subtitle),
    plotOptions: {
      pie: {
        innerSize: '60%',
        depth: 45,
        dataLabels: {
          enabled: true,
          formatter: function () {
            const unitMul = formatUnitMult(
              disaggregationData,
              dataSource?.[0]?.[disaggregations.UnitMult]
            )
            return `${this.point.name}: ${formatNumberOnUnitMeasure({
              value: this.y,
              unitMeasure: selectedUnitMeasure,
              decimals: dataSource?.[0]?.[disaggregations.Decimals],
            })}${`${unitMul ? ` ${unitMul}` : ''}`}${formatUnitMeasure(
              selectedUnitMeasure,
              unitMeasureName,
              shortLabel
            )}`
          },
          style: {
            fontSize: '0.75rem',
            textOutline: 'none',
          },
          distance: 20,
        },
        borderWidth: 2,
        borderColor: '#ffffff',
        showInLegend: true,
      },
    },
    series: chartOptions?.series || [
      {
        name: indicatorMetadata?.series_description?.name || '',
        data: [],
      },
    ],
    legend: {
      enabled: true,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      itemStyle: {
        fontSize: '0.875rem',
        color: '#333',
      },
      itemHoverStyle: {
        color: '#000',
      },
      maxHeight: 60,
    },
    tooltip: {
      backgroundColor: '#fff',
      borderWidth: 0.5,
      borderColor: '#CED4DE',
      shadow: true,
      useHTML: true,
      formatter: function () {
        const unitMul = formatUnitMult(
          disaggregationData,
          dataSource?.[0]?.[disaggregations.UnitMult]
        )
        return `
        <div class="tui_chart_tooltip" style="padding: 6px 8px; box-shadow: 4px 4px 4px rgba(0, 0, 0, 0.15);">
          <div class="tui_chart_tooltip_title" style="font-size: 14px; font-weight: 600; border-bottom: 1px solid #CED4DE; padding-bottom: 4px; margin-bottom: 4px;">
            ${this.point.name}
          </div>
          <div style="font-size: 16px; font-weight: 700;">
            ${formatNumberOnUnitMeasure({
              value: this.y,
              unitMeasure: selectedUnitMeasure || this.point.unitMeasureCode,
              decimals: dataSource?.[0]?.[disaggregations.Decimals],
              isCommaSeparatedFormat: true,
            })}${`${unitMul ? ` ${unitMul}` : ''}`}${formatUnitMeasure(
              selectedUnitMeasure || this.point.unitMeasureCode,
              unitMeasureName ||
                unitMeasureList.find(
                  (item) => item.code === this.point?.unitMeasureCode
                )?.description ||
                '',
              shortLabel ||
                unitMeasureList.find(
                  (item) => item.code === this.point?.unitMeasureCode
                )?.short_label ||
                ''
            )}
          </div>
        </div>
      `
      },
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: true,
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            chart: {
              height: 350,
            },
            legend: {
              layout: 'vertical',
              align: 'right',
              verticalAlign: 'middle',
              maxHeight: null,
            },
            plotOptions: {
              pie: {
                dataLabels: {
                  enabled: false,
                },
              },
            },
          },
        },
      ],
    },
  }

  return (
    <div className="tui__chart_content">
      {!noData ? (
        <div>
          <figure className="highcharts-figure">
            <HighchartsReact
              key={`highcharts-${chartKey}-${JSON.stringify(
                chartOptions?.series
              )}`}
              highcharts={Highcharts}
              options={options}
            />
          </figure>
        </div>
      ) : (
        <NoDataImage
          imageURL={noDataImageURL}
          height="345px"
          width="100%"
          message={
            selectedCountries.length === 0 &&
            selectedAverage[chartType] === 'NONE'
              ? 'Select a country or economy to view data'
              : 'No data available'
          }
        />
      )}
    </div>
  )
}

export default PieChart
